import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gogLink } from '../lib/affiliate';
import { gameHref } from '../lib/gameLink';
import { ebayPhysicalUrl } from '../lib/physicalLink';
import { getRescueLinks, suggestRescueLink } from '../lib/api/rescueApi';

const RESCUE_TYPE_KEY = {
  private_server: 'rescue_private_server',
  community_patch: 'rescue_community_patch',
  offline_fix: 'rescue_offline_fix',
  archive: 'rescue_archive',
};

const JACKET = (appId) =>
  `https://cdn.cloudflare.steamstatic.com/steam/apps/${appId}/header.jpg`;

// Quels jeux appartiennent à chaque catégorie (mode tuile).
const FILTERS = {
  delisted: (i) => i.riskType === 'delisted',
  always_online: (i) => i.riskType === 'always_online',
  server_risk: (i) => i.riskType === 'server_risk',
  savable: (i) => (i.savable || i.gogUrl) && i.riskType !== 'safe',
  safe: (i) => i.riskType === 'safe',
};

// modal = { category } (tuile -> liste) | { item } (carte -> un seul jeu) | null
function CategoryModal({ modal, verdict, onClose }) {
  const { t } = useTranslation();
  const [rescue, setRescue] = useState([]);
  const [suggest, setSuggest] = useState(null); // { type, title, url } | null
  const [suggestDone, setSuggestDone] = useState(false);

  useEffect(() => {
    if (!modal) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [modal, onClose]);

  // Verrouille le scroll du fond tant que la fiche est ouverte (repris à la fermeture).
  // On compense la largeur de la scrollbar pour éviter un saut de mise en page.
  useEffect(() => {
    if (!modal) return undefined;
    const { style } = document.body;
    const prevOverflow = style.overflow;
    const prevPaddingRight = style.paddingRight;
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    style.overflow = 'hidden';
    if (scrollbarW > 0) style.paddingRight = `${scrollbarW}px`;
    return () => {
      style.overflow = prevOverflow;
      style.paddingRight = prevPaddingRight;
    };
  }, [modal]);

  // Liens de préservation vérifiés pour le jeu ouvert (vue "un seul jeu").
  const openedAppId = modal && modal.item ? modal.item.appId : null;
  const openedName = modal && modal.item ? modal.item.name : '';
  const isDemo = !!verdict?.isDemo;
  useEffect(() => {
    setRescue([]);
    setSuggest(null);
    setSuggestDone(false);
    if (!openedAppId) return undefined;
    // En démo : un exemple concret (basé sur le jeu ouvert) pour montrer que des serveurs
    // communautaires peuvent apparaître ici.
    if (isDemo) {
      setRescue([
        {
          id: -1,
          type: 'private_server',
          title: t('rescue.demo_example_title', { game: openedName }),
          url: '#',
          description: t('rescue.demo_example_desc'),
        },
      ]);
      return undefined;
    }
    let alive = true;
    getRescueLinks(openedAppId)
      .then(({ data }) => alive && setRescue(data || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [openedAppId, openedName, isDemo, t]);

  const submitSuggest = async (e) => {
    e.preventDefault();
    if (verdict?.isDemo) return; // pas d'envoi réel en mode démo
    if (!suggest || !suggest.title?.trim() || !suggest.url?.trim()) return;
    try {
      await suggestRescueLink({
        steamAppId: openedAppId,
        type: suggest.type || 'private_server',
        title: suggest.title,
        url: suggest.url,
      });
      setSuggestDone(true);
      setSuggest(null);
    } catch {
      /* silencieux : on ne bloque pas l'expérience */
    }
  };

  if (!modal) return null;

  const item = modal.item || null;
  const category = item ? item.riskType : modal.category;
  const tagKey = category === 'savable' ? 'safe' : category;

  // Mode tuile : liste des jeux de la catégorie.
  const source = verdict.library && verdict.library.length ? verdict.library : verdict.items || [];
  const list = !item && category ? source.filter(FILTERS[category] || (() => false)) : [];

  return (
    <div className="catmodal" onClick={onClose}>
      <div
        className={`catmodal__panel catmodal__panel--${category}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button type="button" className="catmodal__close" onClick={onClose} aria-label={t('catinfo.close')}>
          ×
        </button>

        {item ? (
          // ---------- Un seul jeu (clic sur une carte) ----------
          <>
            <img
              className="catmodal__hero"
              src={JACKET(item.appId)}
              alt=""
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div className="catmodal__tagrow">
              <span className={`catmodal__tag catmodal__tag--${category}`}>{t(`risk.${tagKey}`)}</span>
              {/* Lien vers la fiche publique (SEO) du jeu — pas pour les "safe" (pas de page).
                  Les rares jeux hors base (jeux vitrine démo) affichent une page "pas encore répertorié". */}
              {item.riskType !== 'safe' && (
                <a className="catmodal__sheet" href={gameHref(item.appId, item.name)}>
                  {t('verdict.viewsheet')} →
                </a>
              )}
            </div>
            <h2 className="catmodal__title">{item.name}</h2>
            <p className="catmodal__desc">{t(`catinfo.${category}`)}</p>

            {item.savable && item.riskType !== 'safe' && (
              <p className="catmodal__good">{t('catinfo.savable')}</p>
            )}
            {item.gogUrl && (
              <a className="btn btn--mint catmodal__buy" href={gogLink(item.gogUrl)} target="_blank" rel="noopener noreferrer">
                {t('cta.button')}
              </a>
            )}
            {/* Physique = propriété totale, surtout pour un jeu délisté. Lien affilié eBay. */}
            {item.riskType !== 'safe' && (
              <a
                className="catmodal__physical"
                href={ebayPhysicalUrl(item.name)}
                target="_blank"
                rel="sponsored noopener noreferrer nofollow"
              >
                <span className="catmodal__physical-cta">🎮 {t('physical.cta')}</span>
                <span className="catmodal__physical-note">{t('physical.tagline')}</span>
              </a>
            )}

            {item.riskType !== 'safe' && (
              <div className="catmodal__rescue">
                {rescue.length > 0 && (
                  <>
                    <div className="catmodal__rescue-title">
                      🔧 {t('rescue.title')}
                      {isDemo && <span className="catmodal__rescue-demo">{t('rescue.demo_example_note')}</span>}
                    </div>
                    <ul className="catmodal__rescue-list">
                      {rescue.map((r) => (
                        <li key={r.id} className="catmodal__rescue-item">
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="catmodal__rescue-link"
                            onClick={(e) => { if (r.url === '#') e.preventDefault(); }}
                          >
                            <span className="catmodal__rescue-name">{r.title}</span>
                            <span className="catmodal__rescue-type">
                              {t(`rescue.${RESCUE_TYPE_KEY[r.type] || 'rescue_private_server'}`)}
                            </span>
                          </a>
                          {r.description && <p className="catmodal__rescue-desc">{r.description}</p>}
                        </li>
                      ))}
                    </ul>
                    <p className="catmodal__rescue-warn">{t('rescue.warn')}</p>
                  </>
                )}

                {suggestDone ? (
                  <p className="catmodal__rescue-thanks">{t('rescue.thanks')}</p>
                ) : suggest ? (
                  <form className="catmodal__suggest" onSubmit={submitSuggest}>
                    <select
                      value={suggest.type}
                      onChange={(e) => setSuggest((s) => ({ ...s, type: e.target.value }))}
                    >
                      <option value="private_server">{t('rescue.rescue_private_server')}</option>
                      <option value="community_patch">{t('rescue.rescue_community_patch')}</option>
                      <option value="offline_fix">{t('rescue.rescue_offline_fix')}</option>
                      <option value="archive">{t('rescue.rescue_archive')}</option>
                    </select>
                    <input
                      type="text"
                      placeholder={t('rescue.suggest_title')}
                      value={suggest.title || ''}
                      onChange={(e) => setSuggest((s) => ({ ...s, title: e.target.value }))}
                      required
                    />
                    <input
                      type="url"
                      placeholder="https://..."
                      value={suggest.url || ''}
                      onChange={(e) => setSuggest((s) => ({ ...s, url: e.target.value }))}
                      required
                    />
                    <div className="catmodal__suggest-actions">
                      {verdict.isDemo && (
                        <span className="catmodal__suggest-demo">{t('rescue.demo_disabled')}</span>
                      )}
                      <button type="button" className="btn btn--ghost" onClick={() => setSuggest(null)}>
                        {t('rescue.cancel')}
                      </button>
                      <button type="submit" className="btn btn--amber" disabled={verdict.isDemo}>
                        {t('rescue.send')}
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    type="button"
                    className="catmodal__rescue-suggest-btn"
                    onClick={() => setSuggest({ type: 'private_server', title: '', url: '' })}
                  >
                    {rescue.length > 0 ? t('rescue.suggest_more') : t('rescue.suggest_first')}
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          // ---------- Catégorie (clic sur une tuile) ----------
          <>
            <span className={`catmodal__tag catmodal__tag--${category}`}>{t(`risk.${tagKey}`)}</span>
            <h2 className="catmodal__title">{t(`verdict.cat_${category}`)}</h2>
            <p className="catmodal__desc">{t(`catinfo.${category}`)}</p>

            {list.length > 0 ? (
              <>
                <p className="catmodal__count">{t('catinfo.count', { count: list.length })}</p>
                <ul className="catmodal__list">
                  {list.map((i) => {
                    const hours = Math.round((i.playtimeMinutes || 0) / 60);
                    return (
                      <li key={i.appId} className="catmodal__row">
                        <img
                          className="catmodal__jacket"
                          src={JACKET(i.appId)}
                          alt=""
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.visibility = 'hidden';
                          }}
                        />
                        <div className="catmodal__info">
                          <span className="catmodal__name">{i.name}</span>
                          {hours > 0 && (
                            <span className="catmodal__hours">{t('verdict.playtime', { hours })}</span>
                          )}
                        </div>
                        {i.gogUrl ? (
                          <a
                            className="btn btn--mint catmodal__cta"
                            href={gogLink(i.gogUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            GOG
                          </a>
                        ) : (
                          <span className="catmodal__norescue">{t('catinfo.no_alt')}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <p className="catmodal__empty">{t('catinfo.empty')}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CategoryModal;
